import React, { useState, useRef, useEffect } from 'react';
import {
  Fab,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Button,
  Slide,
  Tooltip
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  DeleteOutline as ClearIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BOTTOM_NAV_ROLES } from './MobileBottomNav';

// ─── Role-based default suggestion chips ─────────────────────────────────────
const ROLE_QUESTIONS = {
  principal: [
    "How many students are absent today?",
    "Which teachers have pending leave requests?",
    "What is the fee collection status?",
    "Show me results for the last exam",
    "How many parent meetings are scheduled?",
    "Which class has the lowest attendance?"
  ],
  school_admin: [
    "How many students are absent today?",
    "Which teachers have pending leave requests?",
    "What is the fee collection status?",
    "Show me results for the last exam",
    "How many parent meetings are scheduled?",
    "Which class has the lowest attendance?"
  ],
  teacher: [
    "How many of my students were absent today?",
    "What is my leave balance for this year?",
    "Show upcoming holidays",
    "Any new announcements from the school?",
    "Show marks for the last exam in my class",
    "When is my next parent meeting?"
  ]
};

// ─── Role-aware dynamic follow-up suggestions (after a bot answer) ────────────
const FOLLOW_UP_QUESTIONS = {
  principal: {
    attendance: [
      "Which class has the most absences?",
      "List all absent students by name",
      "What was the attendance rate last week?",
      "Compare attendance across all classes"
    ],
    leave: [
      "How many leaves are approved this month?",
      "Which teacher has the most leaves?",
      "Show all pending leave requests",
      "What types of leave are available?"
    ],
    exam: [
      "Who scored highest overall?",
      "How many students failed?",
      "Show subject-wise average marks",
      "Compare results across classes"
    ],
    meeting: [
      "How many meetings were completed this month?",
      "Which class has the most meetings?",
      "Are there any meetings this week?",
      "Show overdue pending meetings"
    ],
    fee: [
      "How much is outstanding fee?",
      "Which class has most pending fees?",
      "Show recent payment records",
      "What is collection this month?"
    ]
  },
  teacher: {
    attendance: [
      "Which of my students are frequently absent?",
      "List absent students in my class today",
      "What was my class attendance rate last week?",
      "Which student has the worst attendance?"
    ],
    leave: [
      "What is my casual leave balance?",
      "What is my earned leave balance?",
      "How many leaves have I taken this month?",
      "What is the status of my last leave request?"
    ],
    exam: [
      "Who are the top 3 students in my class?",
      "How many of my students failed?",
      "Show subject-wise average for my class",
      "Which student improved the most?"
    ],
    meeting: [
      "What is the status of my upcoming meetings?",
      "How many meetings did I complete this month?",
      "Are there any meetings this week for me?",
      "Which student's parent needs to be contacted?"
    ],
    fee: [
      "Which of my students have pending fees?",
      "How many students in my class paid fees?",
      "Show recent fee payments for my class",
      "What is the fee collection rate in my class?"
    ],
    holiday: [
      "Is there a holiday this week?",
      "How many holidays are left this year?",
      "Show all holidays this month",
      "When is the next long weekend?"
    ],
    announcement: [
      "Are there any urgent announcements?",
      "Show all announcements from this month",
      "What was the last announcement about?",
      "Are there any important notices for teachers?"
    ]
  }
};

// ─── Action shortcuts: keyword → navigation deep-link ────────────────────────
const ACTION_MAP = {
  principal: [
    { keywords: ['absent', 'attendance'], label: 'View Attendance', route: '/principal/attendance' },
    { keywords: ['leave', 'sick', 'casual', 'earned'], label: 'Manage Leaves', route: '/principal/leave-management' },
    { keywords: ['meeting', 'parent'], label: 'Parent Meetings', route: '/principal/parent-meetings' },
    { keywords: ['exam', 'mark', 'score', 'grade', 'result', 'rank', 'fail', 'pass'], label: 'View Marks', route: '/principal/view-marks' },
    { keywords: ['fee', 'payment', 'finance', 'collect'], label: 'Manage Finance', route: '/principal/finance' },
    { keywords: ['teacher'], label: 'Teachers', route: '/principal/teachers' },
    { keywords: ['student'], label: 'Students', route: '/principal/students' }
  ],
  school_admin: [
    { keywords: ['absent', 'attendance'], label: 'View Attendance', route: '/principal/attendance' },
    { keywords: ['leave', 'sick', 'casual', 'earned'], label: 'Manage Leaves', route: '/principal/leave-management' },
    { keywords: ['meeting', 'parent'], label: 'Parent Meetings', route: '/principal/parent-meetings' },
    { keywords: ['exam', 'mark', 'score', 'grade', 'result', 'rank', 'fail', 'pass'], label: 'View Marks', route: '/principal/view-marks' },
    { keywords: ['fee', 'payment', 'finance', 'collect'], label: 'Manage Finance', route: '/principal/finance' }
  ],
  teacher: [
    { keywords: ['absent', 'attendance'], label: 'Mark Attendance', route: '/teacher/attendance' },
    { keywords: ['leave', 'sick', 'casual', 'earned', 'balance'], label: 'My Leaves', route: '/teacher/leave-management' },
    { keywords: ['meeting', 'parent'], label: 'My Meetings', route: '/teacher/meetings' },
    { keywords: ['exam', 'mark', 'score', 'grade', 'result', 'rank', 'fail', 'pass'], label: 'My Classes', route: '/teacher/classes' },
    { keywords: ['holiday', 'vacation'], label: 'Holidays', route: '/teacher/holidays' },
    { keywords: ['announcement', 'notice', 'circular'], label: 'Announcements', route: '/teacher/announcements' }
  ]
};

/** Return up to 2 relevant action shortcuts for a bot answer */
function getShortcuts(content, role) {
  if (!content) return [];
  const lower = content.toLowerCase();
  const map = ACTION_MAP[role] || ACTION_MAP.principal;
  return map
    .filter(a => a.keywords.some(k => lower.includes(k)))
    .slice(0, 2);
}

// Blinking text cursor for streaming messages
const blinkCursor = keyframes`
  0%, 50%  { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

/** Render simple markdown: **bold**, *italic*, and bullet lines */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, li) => {
    const isBullet = /^[\-\*•]\s+/.test(line);
    const content = line.replace(/^[\-\*•]\s+/, '');

    // Parse inline bold/italic
    const parts = [];
    const inlineRe = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let last = 0;
    let m;
    while ((m = inlineRe.exec(content)) !== null) {
      if (m.index > last) parts.push(content.slice(last, m.index));
      if (m[2]) parts.push(<strong key={m.index}>{m[2]}</strong>);
      else if (m[3]) parts.push(<em key={m.index}>{m[3]}</em>);
      last = m.index + m[0].length;
    }
    if (last < content.length) parts.push(content.slice(last));

    if (isBullet) {
      return (
        <Box key={li} component="li" sx={{ ml: 1.5, mb: 0.25, listStyleType: 'disc', display: 'list-item' }}>
          {parts}
        </Box>
      );
    }
    return (
      <Box key={li} component="span" sx={{ display: 'block', mb: line.trim() === '' ? 0.5 : 0 }}>
        {parts}
      </Box>
    );
  });
}

// ─── Main Chatbot Component ───────────────────────────────────────────────────
const Chatbot = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const messagesEndRef = useRef(null);

  const roleSuggestions = ROLE_QUESTIONS[user?.role] || ROLE_QUESTIONS.principal;
  const [suggestions, setSuggestions] = useState(roleSuggestions);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset suggestions when panel closes
  useEffect(() => {
    if (!open) setSuggestions(roleSuggestions);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show personalized welcome message when chat first opens
  useEffect(() => {
    if (open && messages.length === 0) {
      const firstName = user?.name?.split(' ')[0] || 'there';
      setMessages([{
        role: 'assistant',
        content: `Hi ${firstName}! I'm ClassTree AI. Ask me anything about your school data — attendance, exams, leaves, meetings, and more.`,
        timestamp: Date.now()
      }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSuggestions = (answer) => {
    const lower = answer.toLowerCase();
    const roleKey = user?.role === 'teacher' ? 'teacher' : 'principal';
    const followUps = FOLLOW_UP_QUESTIONS[roleKey];

    if (lower.includes('absent') || lower.includes('attendance')) {
      setSuggestions(followUps.attendance);
    } else if (lower.includes('leave') || lower.includes('sick') || lower.includes('casual') || lower.includes('earned')) {
      setSuggestions(followUps.leave);
    } else if (lower.includes('exam') || lower.includes('marks') || lower.includes('%') || lower.includes('score') || lower.includes('result')) {
      setSuggestions(followUps.exam);
    } else if (lower.includes('meeting') || lower.includes('parent')) {
      setSuggestions(followUps.meeting);
    } else if (lower.includes('fee') || lower.includes('payment') || lower.includes('collect')) {
      setSuggestions(followUps.fee);
    } else if (roleKey === 'teacher' && (lower.includes('holiday') || lower.includes('vacation') || lower.includes('weekend'))) {
      setSuggestions(followUps.holiday);
    } else if (roleKey === 'teacher' && (lower.includes('announcement') || lower.includes('notice') || lower.includes('circular'))) {
      setSuggestions(followUps.announcement);
    } else {
      setSuggestions(roleSuggestions);
    }
  };

  // Only show for principal, school_admin, teacher
  if (!user || !['principal', 'school_admin', 'teacher'].includes(user.role)) {
    return null;
  }

  // Teacher gets MobileBottomNav on mobile — dock the FAB above it there
  const hasBottomNav = BOTTOM_NAV_ROLES.includes(user?.role);

  const sendMessage = async (text) => {
    const question = text || input.trim();
    if (!question || loading) return;

    // Capture current history before adding the new user message
    const history = messages.map(({ role, content }) => ({ role, content }));

    setMessages(prev => [...prev, { role: 'user', content: question, timestamp: Date.now() }]);
    setInput('');
    setLoading(true);

    // Optimistically add an empty streaming assistant message
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true, timestamp: Date.now() }]);

    try {
      const token = localStorage.getItem('token');
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiBase}/chatbot/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: question, history })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullAnswer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete last line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') {
            // Mark streaming complete, finalise message
            setMessages(prev => {
              const msgs = [...prev];
              const last = msgs[msgs.length - 1];
              if (last?.streaming) msgs[msgs.length - 1] = { ...last, streaming: false };
              return msgs;
            });
            updateSuggestions(fullAnswer);
            break;
          }
          try {
            const { token: tok, error } = JSON.parse(payload);
            if (error) throw new Error(error);
            if (tok) {
              fullAnswer += tok;
              setMessages(prev => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.streaming) {
                  msgs[msgs.length - 1] = { ...last, content: last.content + tok };
                }
                return msgs;
              });
            }
          } catch (parseErr) {
            // Ignore malformed SSE lines
          }
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'Something went wrong. Please try again.';
      setMessages(prev => {
        const msgs = [...prev];
        const last = msgs[msgs.length - 1];
        if (last?.streaming) {
          msgs[msgs.length - 1] = { ...last, content: errorMsg, isError: true, streaming: false };
        } else {
          msgs.push({ role: 'assistant', content: errorMsg, isError: true });
        }
        return msgs;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        variant="extended"
        color="primary"
        onClick={() => setOpen(prev => !prev)}
        sx={{
          position: 'fixed',
          // Mobile: bottom-left, since every Save/Cancel action row in this
          // app clusters bottom-right (DialogActions default) — the FAB was
          // sitting directly on top of Save buttons there. Desktop untouched.
          bottom: {
            xs: hasBottomNav ? 'calc(56px + env(safe-area-inset-bottom) + 16px)' : 16,
            sm: 24,
          },
          left: { xs: 16, sm: 'auto' },
          right: { xs: 'auto', sm: 24 },
          zIndex: 1400,
          height: { xs: 52, sm: 64 },
          px: { xs: 2, sm: 3 },
          gap: 1,
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 700,
          borderRadius: '32px',
          boxShadow: '0 6px 24px rgba(37, 99, 235, 0.45)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(37, 99, 235, 0.55)',
            transform: 'scale(1.05)'
          },
          transition: 'all 0.2s ease'
        }}
      >
        {open ? <CloseIcon sx={{ fontSize: { xs: 24, sm: 28 } }} /> : <ChatIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />}
        {!open && 'Ask ClassTree'}
      </Fab>

      {/* Chat Panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            // Same left/right flip as the FAB so the panel opens from the same corner on mobile
            bottom: {
              xs: hasBottomNav ? 'calc(56px + env(safe-area-inset-bottom) + 96px)' : 80,
              sm: 104,
            },
            left: { xs: 8, sm: 'auto' },
            right: { xs: 'auto', sm: 24 },
            width: { xs: 'calc(100vw - 16px)', sm: 400 },
            height: { xs: 'calc(100vh - 100px)', sm: 520 },
            maxHeight: { xs: '85vh', sm: '72vh' },
            zIndex: 1400,
            borderRadius: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BotIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                Ask ClassTree
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label="Beta"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  height: 22,
                  fontSize: '0.7rem'
                }}
              />
              {messages.length > 0 && (
                <Tooltip title="Clear chat">
                  <IconButton
                    size="small"
                    onClick={() => { setMessages([]); setSuggestions(roleSuggestions); }}
                    sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            {/* Messages */}
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                onMouseEnter={() => msg.role === 'assistant' && setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end'
                  }}
                >
                  {msg.role === 'assistant' && (
                    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, flexShrink: 0 }}>
                      <BotIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Box sx={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        py: 1,
                        px: 1.5,
                        bgcolor: msg.isError
                          ? 'error.50'
                          : msg.role === 'user'
                            ? 'primary.main'
                            : 'white',
                        color: msg.role === 'user' ? 'white' : 'text.primary',
                        borderRadius: 2,
                        border: msg.role === 'assistant' ? '1px solid' : 'none',
                        borderColor: msg.isError ? 'error.200' : 'grey.200',
                        wordBreak: 'break-word'
                      }}
                    >
                      <Typography variant="body2" component="div" sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                        {msg.role === 'assistant' && !msg.streaming
                          ? renderMarkdown(msg.content)
                          : msg.content
                        }
                        {msg.streaming && (
                          <Box
                            component="span"
                            sx={{ animation: `${blinkCursor} 1s step-end infinite`, ml: '1px' }}
                          >
                            ▌
                          </Box>
                        )}
                      </Typography>
                    </Paper>
                    {msg.timestamp && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', mt: 0.25, px: 0.5 }}>
                        {new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                  {msg.role === 'user' && (
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28, flexShrink: 0 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                </Box>

                {/* Copy button + Action Shortcuts — shown below completed bot messages */}
                {msg.role === 'assistant' && !msg.streaming && !msg.isError && msg.content && (
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, ml: 4.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {hoveredIdx === idx && (
                      <Tooltip title={copiedIdx === idx ? 'Copied!' : 'Copy response'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(msg.content);
                            setCopiedIdx(idx);
                            setTimeout(() => setCopiedIdx(null), 2000);
                          }}
                          sx={{ width: 22, height: 22, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                          {copiedIdx === idx
                            ? <CheckIcon sx={{ fontSize: 13 }} />
                            : <CopyIcon sx={{ fontSize: 13 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {getShortcuts(msg.content, user?.role).map((sc, i) => (
                      <Button
                        key={i}
                        size="small"
                        variant="outlined"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                        onClick={() => navigate(sc.route)}
                        sx={{
                          fontSize: '0.68rem',
                          height: 24,
                          px: 1,
                          py: 0,
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: 'primary.light',
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' }
                        }}
                      >
                        {sc.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}

            {/* Loading indicator — only shown before the first streaming chunk arrives */}
            {loading && !messages.some(m => m.streaming) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
                  <BotIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Paper
                  elevation={0}
                  sx={{ py: 1, px: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', bgcolor: 'white' }}
                >
                  <CircularProgress size={18} />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Persistent Suggestion Chips */}
          {!loading && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                flexShrink: 0,
                bgcolor: 'white',
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              {suggestions.slice(0, 4).map((q, i) => (
                <Chip
                  key={i}
                  label={q}
                  size="small"
                  variant="outlined"
                  onClick={() => sendMessage(q)}
                  sx={{
                    fontSize: '0.68rem',
                    cursor: 'pointer',
                    height: 24,
                    '&:hover': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main' }
                  }}
                />
              ))}
            </Box>
          )}

          {/* Input Area */}
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white',
              flexShrink: 0
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask a question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                multiline
                maxRows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.85rem'
                  }
                }}
              />
              <IconButton
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 36,
                  height: 36,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default Chatbot;
